/**
 * Étape 1 — Profil propriétaire + commune.
 *
 * Le profil détermine les aides accessibles (MPR Décarbonation, Parcours
 * accompagné, Bailleur, Copro, CEE seules) et la commune permet de
 * calibrer le zoning Anah utilisé à l'étape Revenus.
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  Handshake,
  Home,
  Key,
  MapPin,
  Thermometer,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { CitySelect, type CommuneResult } from '../../ui/city-select';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { storeLocation } from '../../../lib/audience-storage';
import {
  ANAH_ZONE_LABELS,
  CLIMATE_ZONE_COLORS,
  CLIMATE_ZONE_LABELS,
  getZoning,
} from '../../../lib/zoning';
import {
  aidsStepCounterLabel,
  useAidsStore,
  type ProfileType,
} from './useAidsStore';

interface ProfileMeta {
  id: ProfileType;
  label: string;
  sublabel: string;
  icon: LucideIcon;
  highlights: string[];
}

const PROFILES: ProfileMeta[] = [
  {
    id: 'occupant',
    label: 'Propriétaire occupant',
    sublabel: "J'habite mon logement principal",
    icon: Home,
    highlights: ['MPR Décarbonation', 'Parcours accompagné', 'CEE', 'Éco-PTZ'],
  },
  {
    id: 'bailleur',
    label: 'Propriétaire bailleur',
    sublabel: 'Logement loué — engagement 6 ans',
    icon: Handshake,
    highlights: ['MPR Bailleur', 'CEE', 'TVA 5,5 %'],
  },
  {
    id: 'copropriete',
    label: 'Copropriété',
    sublabel: 'Syndic / parties communes',
    icon: Building2,
    highlights: ['MPR Copro', 'CEE collectifs', 'Éco-PTZ Copro'],
  },
  {
    id: 'locataire',
    label: 'Locataire',
    sublabel: 'Petits travaux avec accord du bailleur',
    icon: Key,
    highlights: ['CEE avec accord'],
  },
];

export function Step1Profile() {
  const profile = useAidsStore((s) => s.profile);
  const location = useAidsStore((s) => s.location);
  const setProfile = useAidsStore((s) => s.setProfile);
  const setLocation = useAidsStore((s) => s.setLocation);
  const goNext = useAidsStore((s) => s.goNext);

  const [draft, setDraft] = useState<CommuneResult | null>(location);

  // Sync local draft when location store changes (ex: restored from storage).
  useEffect(() => {
    if (location && (!draft || draft.code !== location.code)) {
      setDraft(location);
    }
  }, [location, draft]);

  function onPickCity(c: CommuneResult | null) {
    setDraft(c);
    if (!c) {
      setLocation(null);
      return;
    }
    // Mémorise pour les autres outils du site (header, modal audience).
    const enriched = storeLocation(c);
    setLocation(enriched);
  }

  const canContinue = profile !== null && location !== null;
  const zoning = draft ? getZoning(draft.codeDepartement) : null;

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {aidsStepCounterLabel(0, profile)}
      </p>
      <h2 className="mt-2 h-section">
        Parlons de vous, brièvement
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        On adapte le calcul à votre situation. Aucune donnée n'est partagée tant
        que vous ne confirmez pas l'envoi à l'étape résultat.
      </p>

      {/* ── Sélection du profil ───────────────────────────── */}
      <fieldset className="mt-6 border-0 p-0">
        <legend className="text-[12px] font-semibold text-ink">
          Vous êtes…
        </legend>
        <div role="radiogroup" aria-label="Votre profil" className="mt-3 grid gap-2.5 sm:grid-cols-2">
          {PROFILES.map((p) => {
            const Icon = p.icon;
            const selected = profile === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setProfile(p.id)}
                className={cn(
                  'group relative flex flex-col gap-3 overflow-hidden rounded-xl border-2 bg-white p-4 text-left transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                  selected
                    ? 'border-brand-500 bg-brand-50/70 shadow-[0_4px_24px_-8px_rgba(16,185,129,0.25)]'
                    : 'border-stone-200 hover:-translate-y-px hover:border-stone-300 hover:shadow-[0_4px_16px_-4px_rgba(10,10,10,0.08)]',
                )}
              >
                {selected && (
                  <motion.span
                    layoutId="profile-bg"
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/0 via-brand-50/60 to-accent-50/40"
                    transition={{ duration: 0.3, ease: easeStripe }}
                  />
                )}
                <div className="relative flex items-start gap-3">
                  <span
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-150',
                      selected
                        ? 'bg-brand-100 text-brand-700'
                        : 'bg-stone-100 text-stone-600',
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'text-[14px] font-semibold',
                        selected ? 'text-brand-900' : 'text-ink',
                      )}
                    >
                      {p.label}
                    </p>
                    <p className="mt-0.5 text-[12px] text-stone-500">
                      {p.sublabel}
                    </p>
                  </div>
                  <ArrowRight
                    className={cn(
                      'h-4 w-4 shrink-0 transition-all duration-150',
                      selected
                        ? 'translate-x-0.5 text-brand-500'
                        : 'text-stone-300 group-hover:translate-x-0.5',
                    )}
                  />
                </div>
                <div className="relative flex flex-wrap gap-1">
                  {p.highlights.map((h) => (
                    <span
                      key={h}
                      className={cn(
                        'rounded-md px-1.5 py-0.5 text-[10.5px] font-medium transition-colors',
                        selected
                          ? 'bg-white/80 text-brand-700 ring-1 ring-inset ring-brand-200/60'
                          : 'bg-stone-100/80 text-stone-600',
                      )}
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* ── Sélection de la commune ───────────────────────── */}
      <div className="mt-6">
        <label
          htmlFor="city-input"
          className="flex items-center gap-1.5 text-[12px] font-semibold text-ink"
        >
          <MapPin className="h-3.5 w-3.5 text-brand-600" strokeWidth={1.8} />
          Votre commune
          <span className="ml-auto text-[11px] font-normal text-stone-400">
            indispensable pour les plafonds MPR
          </span>
        </label>
        <div className="mt-2">
          <CitySelect
            inputId="city-input"
            value={draft}
            onChange={onPickCity}
            placeholder="Ex : Lyon, Bordeaux, Rennes…"
          />
        </div>

        {/* Indicateurs de zoning sous l'input */}
        {zoning && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: easeStripe }}
            className="mt-2.5 flex flex-wrap items-center gap-1.5"
          >
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold',
                CLIMATE_ZONE_COLORS[zoning.climateZone],
              )}
            >
              <Thermometer className="h-2.5 w-2.5" strokeWidth={2} />
              {CLIMATE_ZONE_LABELS[zoning.climateZone]}
            </span>
            <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10.5px] font-medium text-stone-600">
              {ANAH_ZONE_LABELS[zoning.anahZone]}
            </span>
            <span className="text-[10.5px] text-stone-400">
              · barèmes adaptés à votre département
            </span>
          </motion.div>
        )}
      </div>

      {/* ── CTA ───────────────────────────────────────────── */}
      <Button
        variant="brand"
        size="xl"
        disabled={!canContinue}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <Users className="h-3 w-3" strokeWidth={1.8} />
        ~30 secondes pour finir le calcul
      </p>
    </div>
  );
}
